if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };

      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';

      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();

      // Listener natif slide
      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      }

      // Listener natif thumbs click
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (!btn) return;
          btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if ((this.dataset.desktopLayout || '').includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      // ✅ IMPORTANT : on ne dépend plus du thème pour déclencher le filtrage
      this.bindVariantChange();

      // ✅ AU CHARGEMENT : si aucune couleur cochée → coche "Rose"
      requestAnimationFrame(() => this.ensureDefaultColorSelected('Rose'));
      setTimeout(() => this.ensureDefaultColorSelected('Rose'), 150);

      // ✅ AU CHARGEMENT : applique le filtre (après que Shopify ait rendu)
      setTimeout(() => this.applyColorFilter(), 80);
      setTimeout(() => this.applyColorFilter(), 220);
    }

    // ---------------- Utils ----------------
    norm(s) {
      return (s || '')
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
    }

    getVariantSelects() {
      return (
        document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`) ||
        document.querySelector('variant-selects')
      );
    }

    findColorBlock(vs) {
      if (!vs) return null;

      // si data-option-name existe
      const byData =
        vs.querySelector('.product-form__input[data-option-name="couleur"]') ||
        vs.querySelector('.product-form__input[data-option-name="color"]') ||
        vs.querySelector('.product-form__input[data-option-name="colour"]');

      if (byData) return byData;

      // sinon via label
      const blocks = Array.from(vs.querySelectorAll('.product-form__input'));
      return blocks.find((b) => {
        const label = b.querySelector('.product-form__label-container .form__label');
        if (!label) return false;
        const t = this.norm(label.textContent);
        return t === 'couleur' || t === 'color' || t === 'colour';
      });
    }

    getSelectedColor() {
      const vs = this.getVariantSelects();
      const colorBlock = this.findColorBlock(vs);
      if (!colorBlock) return '';

      // radios
      const checked = colorBlock.querySelector('input[type="radio"]:checked');
      if (checked) return this.norm(checked.value);

      // select
      const select = colorBlock.querySelector('select');
      if (select && select.value) return this.norm(select.value);

      return '';
    }

    ensureDefaultColorSelected(defaultColor = 'Rose') {
      const vs = this.getVariantSelects();
      const colorBlock = this.findColorBlock(vs);
      if (!vs || !colorBlock) return;

      // déjà sélectionné ?
      const alreadyChecked = colorBlock.querySelector('input[type="radio"]:checked');
      const select = colorBlock.querySelector('select');
      if (alreadyChecked) return;
      if (select && select.value) return;

      const wanted = this.norm(defaultColor);

      // radios
      const radios = Array.from(colorBlock.querySelectorAll('input[type="radio"]'));
      if (radios.length) {
        const target = radios.find(r => this.norm(r.value) === wanted);
        if (target) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      // dropdown
      if (select) {
        const opt = Array.from(select.options).find(o => this.norm(o.value) === wanted);
        if (opt) {
          select.value = opt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Récupère un "alt" fiable pour matcher (dataset.alt OU img alt)
    getSlideAlt(slide) {
      const d = slide?.dataset?.alt;
      if (d) return this.norm(d);

      const img = slide.querySelector?.('img[alt]');
      if (img) return this.norm(img.getAttribute('alt'));

      return '';
    }

    // Slides à filtrer (viewer + thumbs + counter)
    getAllSlides() {
      // On garde tes classes d'origine (Shrine/Dawn-like)
      return Array.from(this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link'));
    }

    showEl(el, animate = true) {
      if (!el) return;
      el.classList.remove('hidden');
      el.style.display = '';
      el.style.pointerEvents = '';

      if (!animate) {
        el.classList.remove('rb-variant-enter');
        return;
      }

      el.classList.add('rb-variant-enter');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.classList.remove('rb-variant-enter');
        });
      });
    }

    hideEl(el) {
      if (!el) return;
      el.classList.add('hidden');
      el.style.display = 'none';
      el.classList.remove('rb-variant-enter');
    }

    // ---------------- Filtrage ----------------
    applyColorFilter() {
      const slides = this.getAllSlides();
      if (!slides.length) return;

      const color = this.getSelectedColor();

      // Si pas de couleur => on ne casse pas la galerie :
      // - soit Shopify a déjà une variante, soit user pas encore cliqué.
      // Ici on laisse visible (mais on ne force pas "show all" si déjà filtré).
      if (!color) return;

      // Match: alt exactement "rose" OU alt qui contient "rose"
      // (plus robuste si tes alts sont "rose 1", "cardigan rose", etc.)
      const isMatch = (alt) => {
        if (!alt) return false;
        return alt === color || alt.startsWith(color + ' ') || alt.includes(' ' + color) || alt.includes(color);
      };

      // Compter matches
      let matches = 0;
      for (const s of slides) {
        const alt = this.getSlideAlt(s);
        if (alt === 'always_display' || isMatch(alt)) matches++;
      }

      // 0 match => sécurité : ne rien cacher (évite galerie vide)
      if (matches === 0) return;

      // Apply
      for (const s of slides) {
        const alt = this.getSlideAlt(s);
        if (alt === 'always_display' || isMatch(alt)) this.showEl(s, true);
        else this.hideEl(s);
      }

      // Si le média actif est caché, on active le 1er visible
      this.ensureActiveVisibleMedia();
    }

    ensureActiveVisibleMedia() {
      if (!this.elements.viewer) return;

      const active = this.elements.viewer.querySelector('[data-media-id].is-active');
      const activeHidden = active && (active.classList.contains('hidden') || active.style.display === 'none');

      if (!activeHidden) return;

      const firstVisible = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
        .find(el => !el.classList.contains('hidden') && el.style.display !== 'none');

      if (firstVisible) {
        const mediaId = firstVisible.getAttribute('data-media-id');
        if (mediaId) this.setActiveMedia(mediaId, false);
      }
    }

    bindVariantChange() {
      const vs = this.getVariantSelects();
      if (!vs) return;

      // Dès qu’une option change (couleur/tailles), on ré-applique le filtre couleur
      vs.addEventListener('change', () => {
        // Laisse Shopify/JS mettre à jour la variante, puis filtre
        requestAnimationFrame(() => this.applyColorFilter());
      });
    }

    // ---------------- Galerie native ----------------
    onSlideChanged(event) {
      if (!this.elements.thumbnails) return;
      const current = event?.detail?.currentElement;
      const mediaId = current?.dataset?.mediaId;
      if (!mediaId) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      // (On garde ta signature, même si on ne dépend plus de filtering/currentVariant)

      const viewer = this.elements.viewer;
      if (!viewer) return;

      const activeMedia = viewer.querySelector(`[data-media-id="${mediaId}"]`);

      viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      if (activeMedia) activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia && activeMedia) {
        activeMedia.parentElement.prepend(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }

        if (viewer.slider) viewer.resetPages();
      }

      this.preventStickyHeader();

      window.setTimeout(() => {
        if (this.elements.thumbnails && activeMedia) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }

        if (!filtering && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
          if (activeMedia) activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      });

      if (activeMedia) this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;

      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail);

      if (activeMedia && activeThumbnail) {
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));

      const btn = thumbnail.querySelector('button');
      if (btn) btn.setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;

      if (this.elements.thumbnails.vertical) {
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
      } else {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem?.querySelector?.('.product__modal-opener--image img');
      if (!image || !this.elements.liveRegion) return;

      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };

      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer?.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
    }
  });
}
